// src/pages/solicitudes/VerSolicitud.js
import React, { useEffect, useState } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Tab from 'react-bootstrap/Tab'
import Tabs from 'react-bootstrap/Tabs'
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'
import Badge from 'react-bootstrap/Badge'
import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'
import { Offcanvas } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { PanelFooter, PanelHeader } from 'components/Panel'
import api from 'api/api'
import { toDisplayDate, toDisplayDateTime } from 'utils/date'
import { formatCurrency } from 'utils/format'

// Lightbox
import Lightbox from 'yet-another-react-lightbox'
import 'yet-another-react-lightbox/styles.css'
// Plugins opcionales
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails'
import 'yet-another-react-lightbox/plugins/thumbnails.css'

import './VerSolicitud.css'

function VerSolicitud({ solicitudId, onClose }) {
	const [loading, setLoading] = useState(false)
	const [solicitud, setSolicitud] = useState(null)

	// Estado para el lightbox
	const [lightboxOpen, setLightboxOpen] = useState(false)
	const [lightboxIndex, setLightboxIndex] = useState(0)

	useEffect(() => {
		if (solicitudId) {
			cargarSolicitud()
		}
	}, [solicitudId])

	const cargarSolicitud = async () => {
		try {
			setLoading(true)
			const { datos, error } = await api.get(`a/solicitudes/${solicitudId}`)

			if (error) {
				toast.error('Error al cargar solicitud: ' + error)
				return
			}

			setSolicitud(datos)
		} catch (error) {
			toast.error('Error de conexion')
			console.error(error)
		} finally {
			setLoading(false)
		}
	}

	// Abrir lightbox en una imagen especifica
	const abrirLightbox = (index) => {
		setLightboxIndex(index)
		setLightboxOpen(true)
	}

	// Preparar imagenes para el lightbox
	const getImagenesLightbox = () => {
		if (!solicitud?.multimedia) return []

		return solicitud.multimedia.map((media) => ({
			src: process.env.REACT_APP_API_URL + 'uploads/' + media.url,
			alt: `Imagen ${media.id}`
		}))
	}

	// Renderizar badge de estado
	const renderEstadoBadge = (estado) => {
		const config = {
			pendiente: { bg: 'secondary', text: 'Pendiente' },
			confirmando_pago: { bg: 'info', text: 'Confirmando pago' },
			pagado: { bg: 'primary', text: 'Pagado' },
			aceptado: { bg: 'success', text: 'Publicado' },
			agendado: { bg: 'warning', text: 'Agendado' },
			completado: { bg: 'success', text: 'Completado' },
			cancelado: { bg: 'danger', text: 'Cancelado' },
			abandonado: { bg: 'dark', text: 'Abandonado' }
		}
		const { bg, text } = config[estado] || { bg: 'secondary', text: estado }
		return <Badge bg={bg} className="ms-2">{text}</Badge>
	}

	// Renderizar badge de tipo
	const renderTipoBadge = (tipo) => {
		const config = {
			urgencia: { bg: 'danger', text: 'Urgencia' },
			presupuesto: { bg: 'primary', text: 'Presupuesto' },
			evaluacion: { bg: 'warning', text: 'Evaluacion' }
		}
		const { bg, text } = config[tipo] || { bg: 'secondary', text: tipo }
		return <Badge bg={bg}>{text}</Badge>
	}

	// Renderizar informacion principal
	const renderInfoPrincipal = () => {
		if (!solicitud) return null

		return (
			<Card className="mb-3">
				<Card.Header className="d-flex justify-content-between align-items-center">
					<div className="d-flex align-items-center">
						{solicitud.categoria.imagen2Url && (
							<img
								src={process.env.REACT_APP_API_URL + 'uploads/' + solicitud.categoria.imagen2Url}
								alt=""
								className="categoria-icon-lg me-3"
							/>
						)}
						<div>
							<h5 className="mb-0">
								{solicitud.categoria.nombre}
								{renderEstadoBadge(solicitud.estado)}
							</h5>
							<small className="text-muted">Solicitud #{solicitud.id}</small>
						</div>
					</div>
					{renderTipoBadge(solicitud.tipo)}
				</Card.Header>
				<Card.Body>
					{/* Descripcion */}
					<div className="mb-3">
						<h6 className="text-muted mb-1">Descripcion del problema</h6>
						<p className="mb-0">{solicitud.descripcion || 'Sin descripcion'}</p>
					</div>

					{/* Opciones seleccionadas */}
					{solicitud.opciones && solicitud.opciones.length > 0 && (
						<div className="mb-3">
							<h6 className="text-muted mb-1">Opciones seleccionadas</h6>
							<div>
								{solicitud.opciones.map((opcion) => (
									<Badge key={opcion.id} bg="light" text="dark" className="me-1 mb-1">
										{opcion.nombre}
									</Badge>
								))}
							</div>
						</div>
					)}

					{/* Disponibilidades */}
					{solicitud.disponibilidades && solicitud.disponibilidades.length > 0 && (
						<div className="mb-3">
							<h6 className="text-muted mb-1">Disponibilidad horaria</h6>
							{solicitud.disponibilidades.map((disp, idx) => (
								<div key={idx} className="small">
									<i className="fa fa-calendar me-2"></i>
									{toDisplayDate(disp.fecha)} - {disp.tipo}
									{disp.horaInicio && ` (${disp.horaInicio} - ${disp.horaFin})`}
								</div>
							))}
						</div>
					)}

					{/* Imagenes con lightbox */}
					{solicitud.multimedia && solicitud.multimedia.length > 0 && (
						<div className="mb-3">
							<h6 className="text-muted mb-1">
								Imagenes adjuntas ({solicitud.multimedia.length})
							</h6>
							<div className="d-flex flex-wrap gap-2">
								{solicitud.multimedia.map((media, index) => (
									<div
										key={media.id}
										className="imagen-adjunta-container"
										onClick={() => abrirLightbox(index)}
									>
										<img
											src={process.env.REACT_APP_API_URL + 'uploads/' + media.url}
											alt={`Imagen ${index + 1}`}
											className="imagen-adjunta"
										/>
										<div className="imagen-overlay">
											<i className="fa fa-search-plus"></i>
										</div>
									</div>
								))}
							</div>
						</div>
					)}
				</Card.Body>
			</Card>
		)
	}

	// Renderizar datos del usuario
	const renderUsuario = () => {
		if (!solicitud || !solicitud.usuario) return null

		const { usuario } = solicitud

		return (
			<Card className="mb-3">
				<Card.Header>
					<i className="fa fa-user me-2"></i>
					Usuario solicitante
				</Card.Header>
				<Card.Body>
					<Row>
						<Col md={6}>
							<p className="mb-1"><strong>Nombre:</strong> {usuario.nombre} {usuario.apellido}</p>
							<p className="mb-1"><strong>Email:</strong> {usuario.email || '-'}</p>
							<p className="mb-1"><strong>Telefono:</strong> {usuario.telefono || '-'}</p>
						</Col>
						<Col md={6}>
							<p className="mb-1"><strong>CI:</strong> {usuario.ci || '-'}</p>
							<p className="mb-1"><strong>Registrado:</strong> {toDisplayDate(usuario.fechaRegistro)}</p>
						</Col>
					</Row>
				</Card.Body>
			</Card>
		)
	}

	// Renderizar ubicacion
	const renderUbicacion = () => {
		if (!solicitud || !solicitud.ubicacion) return null

		const { ubicacion } = solicitud

		return (
			<Card className="mb-3">
				<Card.Header>
					<i className="fa fa-map-marker-alt me-2"></i>
					Ubicacion
				</Card.Header>
				<Card.Body>
					<p className="mb-1"><strong>Direccion:</strong> {ubicacion.direccion || '-'}</p>
					<p className="mb-1"><strong>Ciudad:</strong> {ubicacion.ciudad || '-'}</p>
					<p className="mb-1"><strong>Barrio:</strong> {ubicacion.barrio || '-'}</p>
					{ubicacion.observaciones && (
						<p className="mb-1"><strong>Observaciones:</strong> {ubicacion.observaciones}</p>
					)}
					{ubicacion.lat && ubicacion.lon && (
						<a
							href={`https://www.google.com/maps?q=${ubicacion.lat},${ubicacion.lon}`}
							target="_blank"
							rel="noopener noreferrer"
							className="btn btn-outline-primary btn-sm mt-2"
						>
							<i className="fa fa-external-link-alt me-1"></i>
							Ver en Google Maps
						</a>
					)}
				</Card.Body>
			</Card>
		)
	}

	// Renderizar fechas
	const renderFechas = () => {
		if (!solicitud) return null

		return (
			<Card className="mb-3">
				<Card.Header>
					<i className="fa fa-clock me-2"></i>
					Fechas
				</Card.Header>
				<Card.Body>
					<Row>
						<Col md={6}>
							<p className="mb-1"><strong>Creacion:</strong> {toDisplayDateTime(solicitud.fechaCreacion)}</p>
							{solicitud.fechaCheckout && (
								<p className="mb-1"><strong>Checkout:</strong> {toDisplayDateTime(solicitud.fechaCheckout)}</p>
							)}
							{solicitud.fechaPagoRecibido && (
								<p className="mb-1"><strong>Pago recibido:</strong> {toDisplayDateTime(solicitud.fechaPagoRecibido)}</p>
							)}
							{solicitud.fechaAceptado && (
								<p className="mb-1"><strong>Aceptado:</strong> {toDisplayDateTime(solicitud.fechaAceptado)}</p>
							)}
						</Col>
						<Col md={6}>
							{solicitud.fechaAgendado && (
								<p className="mb-1"><strong>Agendado:</strong> {toDisplayDateTime(solicitud.fechaAgendado)}</p>
							)}
							{solicitud.fechaCompletado && (
								<p className="mb-1"><strong>Completado:</strong> {toDisplayDateTime(solicitud.fechaCompletado)}</p>
							)}
							{solicitud.fechaCancelado && (
								<p className="mb-1"><strong>Cancelado:</strong> {toDisplayDateTime(solicitud.fechaCancelado)}</p>
							)}
						</Col>
					</Row>
					{solicitud.motivoCancelacion && (
						<div className="mt-2 p-2 bg-light rounded">
							<strong>Motivo cancelacion:</strong> {solicitud.motivoCancelacion}
						</div>
					)}
				</Card.Body>
			</Card>
		)
	}

	// Renderizar tab de interacciones
	const renderInteracciones = () => {
		if (!solicitud || !solicitud.interacciones) return null

		const { interacciones } = solicitud

		return (
			<div>
				{/* Resumen */}
				<Row className="mb-3">
					<Col xs={6} md={3}>
						<Card className="text-center">
							<Card.Body className="py-2">
								<h4 className="mb-0">{interacciones.total}</h4>
								<small className="text-muted">Total notificados</small>
							</Card.Body>
						</Card>
					</Col>
					<Col xs={6} md={3}>
						<Card className="text-center">
							<Card.Body className="py-2">
								<h4 className="mb-0 text-success">{interacciones.activas}</h4>
								<small className="text-muted">Activas</small>
							</Card.Body>
						</Card>
					</Col>
					<Col xs={6} md={3}>
						<Card className="text-center">
							<Card.Body className="py-2">
								<h4 className="mb-0 text-info">{interacciones.vistas}</h4>
								<small className="text-muted">Han visto</small>
							</Card.Body>
						</Card>
					</Col>
					<Col xs={6} md={3}>
						<Card className="text-center">
							<Card.Body className="py-2">
								<h4 className="mb-0 text-danger">{interacciones.rechazadas}</h4>
								<small className="text-muted">Rechazadas</small>
							</Card.Body>
						</Card>
					</Col>
				</Row>

				{/* Lista de interacciones */}
				{interacciones.lista.length === 0 ? (
					<p className="text-muted text-center">No hay fixers notificados</p>
				) : (
					<Table striped hover size="sm" responsive>
						<thead>
							<tr>
								<th>Fixer</th>
								<th>Calificacion</th>
								<th>Vistas</th>
								<th>Primera vista</th>
								<th>Estado</th>
							</tr>
						</thead>
						<tbody>
							{interacciones.lista.map((interaccion) => (
								<tr key={interaccion.id} className={interaccion.rechazada ? 'table-danger' : ''}>
									<td>
										<div>
											<strong>{interaccion.fixer.nombre}</strong>
											{interaccion.fixer.telefono && (
												<small className="d-block text-muted">{interaccion.fixer.telefono}</small>
											)}
										</div>
									</td>
									<td>
										{interaccion.fixer.calificacion ? (
											<span>
												{interaccion.fixer.calificacion.toFixed(1)}
												<i className="fa fa-star text-warning ms-1"></i>
											</span>
										) : (
											<span className="text-muted">N/A</span>
										)}
									</td>
									<td>
										<Badge bg={interaccion.cantidadVistas > 0 ? 'info' : 'secondary'}>
											{interaccion.cantidadVistas}
										</Badge>
									</td>
									<td>
										{interaccion.fechaPrimeraVista
											? toDisplayDateTime(interaccion.fechaPrimeraVista)
											: <span className="text-muted">No visto</span>
										}
									</td>
									<td>
										{interaccion.rechazada ? (
											<Badge bg="danger">Rechazada</Badge>
										) : interaccion.activa ? (
											<Badge bg="success">Activa</Badge>
										) : (
											<Badge bg="secondary">Inactiva</Badge>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</Table>
				)}
			</div>
		)
	}

	// Renderizar tab de propuestas
	const renderPropuestas = () => {
		if (!solicitud || !solicitud.propuestas) return null

		const { propuestas } = solicitud

		return (
			<div>
				{/* Resumen */}
				<div className="mb-3">
					<Badge bg="primary" className="me-2">
						{propuestas.total} propuesta{propuestas.total !== 1 ? 's' : ''} recibida{propuestas.total !== 1 ? 's' : ''}
					</Badge>
				</div>

				{/* Lista de propuestas */}
				{propuestas.lista.length === 0 ? (
					<p className="text-muted text-center">No hay propuestas recibidas</p>
				) : (
					<div>
						{propuestas.lista.map((propuesta) => (
							<Card key={propuesta.id} className="mb-2">
								<Card.Body>
									<div className="d-flex justify-content-between align-items-start mb-2">
										<div>
											<strong>{propuesta.fixer.nombre}</strong>
											{propuesta.fixer.calificacion && (
												<span className="ms-2">
													{propuesta.fixer.calificacion.toFixed(1)}
													<i className="fa fa-star text-warning ms-1"></i>
												</span>
											)}
										</div>
										<div>
											{renderEstadoPropuestaBadge(propuesta.estado)}
										</div>
									</div>

									<Row>
										<Col md={6}>
											<p className="mb-1">
												<strong>Precio:</strong>{' '}
												<span className="text-success fs-5">
													{formatCurrency(propuesta.precio)}
												</span>
											</p>
											<p className="mb-1">
												<strong>Fecha propuesta:</strong>{' '}
												{toDisplayDateTime(propuesta.fechaPropuesta)}
											</p>
										</Col>
										<Col md={6}>
											<p className="mb-1">
												<strong>Negociacion:</strong>{' '}
												<Badge bg="secondary">{propuesta.negociacion.estado}</Badge>
											</p>
											{propuesta.negociacion.numContrapropuestas > 0 && (
												<p className="mb-1">
													<strong>Contrapropuestas:</strong>{' '}
													{propuesta.negociacion.numContrapropuestas}/{propuesta.negociacion.maxContrapropuestas}
												</p>
											)}
										</Col>
									</Row>

									{propuesta.descripcion && (
										<div className="mt-2 p-2 bg-light rounded">
											<small><strong>Descripcion:</strong> {propuesta.descripcion}</small>
										</div>
									)}
								</Card.Body>
							</Card>
						))}
					</div>
				)}
			</div>
		)
	}

	// Renderizar badge de estado de propuesta
	const renderEstadoPropuestaBadge = (estado) => {
		const config = {
			pendiente: { bg: 'warning', text: 'Pendiente' },
			aceptada: { bg: 'success', text: 'Aceptada' },
			rechazada: { bg: 'danger', text: 'Rechazada' },
			expirada: { bg: 'secondary', text: 'Expirada' },
			completada: { bg: 'info', text: 'Completada' }
		}
		const { bg, text } = config[estado] || { bg: 'secondary', text: estado }
		return <Badge bg={bg}>{text}</Badge>
	}

	return (
		<Offcanvas
			show={true}
			onHide={onClose}
			placement="end"
			scroll={true}
			className="offcanvas-solicitud"
		>
			<PanelHeader
				onClick={onClose}
				iconClass="fa-arrow-left"
				btnText="Atras"
				text="Detalle de solicitud"
			/>
			<Offcanvas.Body id="ver-solicitud">
				{loading ? (
					<div className="text-center my-5">
						<Spinner
							animation="border"
							size="lg"
							role="status"
							aria-hidden="true"
							variant="primary"
						/>
						<p className="mt-2">Cargando solicitud...</p>
					</div>
				) : solicitud ? (
					<>
						{renderInfoPrincipal()}

						<Tabs defaultActiveKey="informacion" className="mb-3">
							<Tab eventKey="informacion" title="Informacion">
								{renderUsuario()}
								{renderUbicacion()}
								{renderFechas()}
							</Tab>
							<Tab
								eventKey="interacciones"
								title={
									<span>
										Difusion
										{solicitud.interacciones && (
											<Badge bg="secondary" className="ms-1">
												{solicitud.interacciones.total}
											</Badge>
										)}
									</span>
								}
							>
								{renderInteracciones()}
							</Tab>
							<Tab
								eventKey="propuestas"
								title={
									<span>
										Propuestas
										{solicitud.propuestas && (
											<Badge bg="success" className="ms-1">
												{solicitud.propuestas.total}
											</Badge>
										)}
									</span>
								}
							>
								{renderPropuestas()}
							</Tab>
						</Tabs>
					</>
				) : (
					<p className="text-muted text-center">No se pudo cargar la solicitud</p>
				)}
			</Offcanvas.Body>
			<PanelFooter>
				<Button variant="outline-primary" onClick={onClose}>
					Cerrar
				</Button>
			</PanelFooter>

			{/* Lightbox para imagenes */}
			<Lightbox
				open={lightboxOpen}
				close={() => setLightboxOpen(false)}
				index={lightboxIndex}
				slides={getImagenesLightbox()}
				plugins={[Zoom, Thumbnails]}
				zoom={{
					maxZoomPixelRatio: 3,
					scrollToZoom: true
				}}
				thumbnails={{
					position: 'bottom',
					width: 80,
					height: 60
				}}
			/>
		</Offcanvas>
	)
}

export default VerSolicitud